/* Header: desktop dropdown / mega menu behaviour and the drill-down menu drawer. */
(function () {
  'use strict';

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  class HeaderComponent extends HTMLElement {
    connectedCallback() {
      this.dropdowns = Array.from(this.querySelectorAll('[data-nav-dropdown]'));
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.timers = new WeakMap();

      this.dropdowns.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (details.open) this.closeAll(details);
        });
        details.querySelector('summary').addEventListener('click', (event) => {
          // Avoid closing a panel that hover just opened.
          if (finePointer.matches && details.open && Date.now() - (details._hoverOpenedAt || 0) < 700) event.preventDefault();
        });
        details.addEventListener('mouseenter', () => this.hoverOpen(details));
        details.addEventListener('mouseleave', () => this.hoverClose(details));
      });

      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('keydown', this.onKeydown);

      // Expose the sticky header height so sticky sidebars/toolbars sit below it.
      this.sectionEl = this.closest('.section-header');
      this.setOffset = () => {
        if (!this.sectionEl) return;
        const sticky = getComputedStyle(this.sectionEl).position === 'sticky';
        const offset = sticky ? this.sectionEl.offsetHeight + 12 : 16;
        document.documentElement.style.setProperty('--header-offset', `${offset}px`);
      };
      this.resizeObserver = new ResizeObserver(this.setOffset);
      if (this.sectionEl) this.resizeObserver.observe(this.sectionEl);
      this.setOffset();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    hoverOpen(details) {
      if (!finePointer.matches) return;
      clearTimeout(this.timers.get(details));
      this.timers.set(
        details,
        setTimeout(() => {
          if (!details.open) details._hoverOpenedAt = Date.now();
          details.open = true;
        }, 120)
      );
    }

    hoverClose(details) {
      if (!finePointer.matches) return;
      clearTimeout(this.timers.get(details));
      this.timers.set(
        details,
        setTimeout(() => {
          if (!details.contains(document.activeElement) || document.activeElement.tagName === 'SUMMARY') details.open = false;
        }, 180)
      );
    }

    closeAll(except) {
      this.dropdowns.forEach((d) => {
        if (d !== except) d.open = false;
      });
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.closeAll();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const open = this.dropdowns.find((d) => d.open);
      if (open) {
        open.open = false;
        open.querySelector('summary').focus();
      }
    }
  }
  if (!customElements.get('header-component')) customElements.define('header-component', HeaderComponent);

  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      // The header is sticky (its own stacking context), which would put the page overlay above
      // this drawer. Move the drawer to <body> so it always sits above the overlay.
      if (this.parentElement !== document.body) {
        document.querySelectorAll('body > menu-drawer').forEach((old) => {
          if (old !== this) old.remove();
        });
        document.body.appendChild(this);
        return;
      }
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-menu-open]');
        if (opener) {
          event.preventDefault();
          this.openPanel(opener);
          return;
        }
        const back = event.target.closest('[data-menu-back]');
        if (back) {
          event.preventDefault();
          this.closePanel(back.closest('[data-menu-panel]'));
        }
      });
      this.addEventListener('drawer:close', () => {
        window.setTimeout(() => this.reset(), 250);
      });
    }

    openPanel(opener) {
      const panel = this.querySelector(`#${CSS.escape(opener.getAttribute('data-menu-open'))}`);
      if (!panel) return;
      const parent = opener.closest('[data-menu-panel]');
      panel._opener = opener;
      panel.inert = false;
      panel.classList.add('is-active');
      opener.setAttribute('aria-expanded', 'true');
      if (parent) {
        parent.inert = true;
        parent.classList.add('is-behind');
      }
      window.setTimeout(() => {
        const back = panel.querySelector('[data-menu-back]');
        if (back) back.focus({ preventScroll: true });
      }, 200);
    }

    closePanel(panel) {
      if (!panel || panel.getAttribute('data-menu-panel') === 'root') return;
      const opener = panel._opener;
      panel.classList.remove('is-active');
      panel.inert = true;
      const parentId = panel.getAttribute('data-menu-parent');
      const parent = parentId ? this.querySelector(`#${CSS.escape(parentId)}`) : this.querySelector('[data-menu-panel="root"]');
      if (parent) {
        parent.inert = false;
        parent.classList.remove('is-behind');
      }
      if (opener) {
        opener.setAttribute('aria-expanded', 'false');
        opener.focus({ preventScroll: true });
      }
    }

    reset() {
      if (this.classList.contains('is-open')) return;
      this.querySelectorAll('[data-menu-panel]').forEach((panel) => {
        const isRoot = panel.getAttribute('data-menu-panel') === 'root';
        panel.classList.remove('is-active', 'is-behind');
        panel.inert = !isRoot;
      });
      this.querySelectorAll('[data-menu-open]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    }
  }
  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);
})();
